import type { VariantProps } from "class-variance-authority"
import type { HTMLAttributes } from "react"
import slugify from "slugify"
import { cn } from "@/utils/cn"
import { wrapperVariants } from "../standard-layout/wrapper"

export interface SectionProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "title" | "content">,
    VariantProps<typeof wrapperVariants> {
  tagline?: string
  title?: string
  description?: string
  notProse?: boolean
  alternate?: boolean
}

export function Section({ alternate, ...props }: SectionProps) {
  if (alternate) {
    return (
      <div className="bg-secondary">
        <SectionInner {...props} />
      </div>
    )
  }

  return <SectionInner {...props} />
}

interface SectionInnerProps extends Omit<SectionProps, "alternate"> {}

function SectionInner({
  tagline,
  title,
  description,
  size,
  notProse = true,
  id,
  className,
  children,
  ...props
}: SectionInnerProps) {
  const sectionId = tagline ? slugify(tagline) : undefined

  return (
    <section
      id={id ?? sectionId}
      className={cn(
        wrapperVariants({ size }),
        "flex flex-col gap-y-16 py-16 lg:gap-y-20 lg:py-20",
        "[[data-slot=article-body]_&]:px-0",
        notProse && "not-prose",
        className,
      )}
      {...props}
    >
      <header className="mx-auto flex flex-col gap-y-3 text-center">
        {tagline && (
          <span className="font-medium font-mono text-primary/80 text-sm uppercase tracking-wider">
            {tagline}
          </span>
        )}
        {title && (
          <h2 className="mx-auto max-w-3xl text-balance font-display font-extrabold text-3xl leading-tight! tracking-tight sm:text-4xl md:text-5xl">
            {title}
          </h2>
        )}
        {description && (
          <p className="mx-auto max-w-180 text-balance text-lg text-muted-foreground leading-7">
            {description}
          </p>
        )}
      </header>

      {children}
    </section>
  )
}
